import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { PageHeader, KpiCard, EmptyState, useConfirm } from "../../components/common";
import { timeAgo } from "../../lib/channels";
import { Star, Plus, Eye, EyeOff, Trash2, MessageSquareQuote, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Stars = ({ n }) => (
  <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />)}</span>
);
const ST = { beklemede: "bg-amber-100 text-amber-800 border-amber-200", yayinda: "bg-emerald-100 text-emerald-800 border-emerald-200", gizli: "bg-slate-100 text-slate-600 border-slate-200" };
const STL = { beklemede: "Onay Bekliyor", yayinda: "Yayında", gizli: "Gizli" };

export default function Reviews() {
  const [ask, confirmEl] = useConfirm();
  const [data, setData] = useState({ items: [], avg: 0, count: 0, job_rating_avg: 0, job_rating_count: 0 });
  const [filter, setFilter] = useState("all");
  const [add, setAdd] = useState(null);
  const [reply, setReply] = useState(null);

  const load = () => api.get("/reviews").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const set = async (r, patch, msg) => { await api.put(`/reviews/${r.id}`, patch); if (msg) toast.success(msg); load(); };
  const remove = async (r) => { if (await ask("Yorum silinsin mi?")) { await api.delete(`/reviews/${r.id}`); load(); } };
  const create = async () => {
    try { await api.post("/reviews", add); setAdd(null); load(); toast.success("Yorum eklendi"); }
    catch { toast.error("Ad ve 1-5 arası puan gerekli"); }
  };
  const shown = data.items.filter((r) => filter === "all" || r.status === filter);
  const pending = data.items.filter((r) => r.status === "beklemede").length;

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <PageHeader eyebrow="Müşteri Hizmetleri" title="Yorumlar & Memnuniyet" subtitle="Sitedeki yorum formundan gelen değerlendirmeleri onaylayın, yanıtlayın ve yayına alın.">
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setAdd({ name: "", rating: 5, comment: "", city: "" })} data-testid="new-review"><Plus className="h-4 w-4 mr-2" />Yorum Ekle</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={Star} label="Yorum Puanı" value={data.avg || "-"} hint={`${data.count} yorum`} tone="amber" />
        <KpiCard icon={CheckCircle2} label="Servis Puanı" value={data.job_rating_avg || "-"} hint={`${data.job_rating_count} değerlendirilen servis`} tone="emerald" />
        <KpiCard icon={MessageSquareQuote} label="Onay Bekleyen" value={pending} tone="blue" />
        <KpiCard icon={Eye} label="Yayında" value={data.items.filter((r) => r.status === "yayinda").length} tone="violet" />
      </div>
      <div className="flex flex-wrap gap-2">
        {["all", "beklemede", "yayinda", "gizli"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>{s === "all" ? "Tümü" : STL[s]}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="reviews-list">
        {shown.length === 0 && <Card className="p-0 border-slate-200 shadow-none lg:col-span-2"><EmptyState icon={Star} title="Yorum yok" /></Card>}
        {shown.map((r) => (
          <Card key={r.id} className="p-5 border border-slate-200 shadow-none">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-slate-900">{r.name}{r.city ? <span className="text-slate-400 font-normal"> • {r.city}</span> : null}</div>
                <div className="flex items-center gap-2 mt-1"><Stars n={r.rating} /><span className="text-xs text-slate-400">{timeAgo(r.created_at)}{r.job_number ? ` • ${r.job_number}` : ""}</span></div>
              </div>
              <span className={`status-pill border ${ST[r.status]}`}>{STL[r.status]}</span>
            </div>
            {r.comment && <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{r.comment}</p>}
            {r.reply && <div className="mt-3 text-sm p-3 rounded-lg bg-blue-50 border border-blue-100"><b className="text-blue-800">Yanıtınız:</b> {r.reply}</div>}
            <div className="flex items-center gap-2 mt-4 flex-wrap">
              {r.status !== "yayinda" && <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200" onClick={() => set(r, { status: "yayinda" }, "Yayına alındı")} data-testid={`review-publish-${r.id}`}><Eye className="h-4 w-4 mr-1.5" />Yayınla</Button>}
              {r.status !== "gizli" && <Button size="sm" variant="outline" onClick={() => set(r, { status: "gizli" }, "Gizlendi")}><EyeOff className="h-4 w-4 mr-1.5" />Gizle</Button>}
              <Button size="sm" variant="outline" onClick={() => setReply({ ...r, reply: r.reply || "" })}>Yanıtla</Button>
              <button className="ml-auto text-slate-400 hover:text-rose-600" onClick={() => remove(r)}><Trash2 className="h-4 w-4" /></button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!add} onOpenChange={(o) => !o && setAdd(null)}>
        <DialogContent>
          {add && (<>
            <DialogHeader><DialogTitle>Yorum Ekle</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Ad Soyad *</Label><Input value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} data-testid="rv-name" /></div>
                <div><Label>Şehir</Label><Input value={add.city} onChange={(e) => setAdd({ ...add, city: e.target.value })} /></div>
              </div>
              <div><Label>Puan</Label><div className="flex gap-1 mt-1">{[1, 2, 3, 4, 5].map((i) => <button key={i} onClick={() => setAdd({ ...add, rating: i })}><Star className={`h-6 w-6 ${i <= add.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}</div></div>
              <div><Label>Yorum</Label><Textarea rows={3} value={add.comment} onChange={(e) => setAdd({ ...add, comment: e.target.value })} /></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setAdd(null)}>Vazgeç</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={create} data-testid="rv-save">Ekle</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
      <Dialog open={!!reply} onOpenChange={(o) => !o && setReply(null)}>
        <DialogContent>
          {reply && (<>
            <DialogHeader><DialogTitle>Yoruma Yanıt</DialogTitle></DialogHeader>
            <Textarea rows={4} value={reply.reply} onChange={(e) => setReply({ ...reply, reply: e.target.value })} />
            <DialogFooter><Button variant="outline" onClick={() => setReply(null)}>Vazgeç</Button><Button className="bg-blue-600 hover:bg-blue-700" onClick={async () => { await set(reply, { reply: reply.reply }, "Yanıt kaydedildi"); setReply(null); }}>Kaydet</Button></DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>
      {confirmEl}
    </div>
  );
}
